import { type TranslationSchema } from '~/locales/resources'
import {
  diffNineNumbers,
  type NineSegment,
  type NineSegmentCounts,
  nineSegments,
  sumNineNumbers,
} from '~/utils'

export const nineSegmentIndicators = [
  // 消極全体
  'totalNegative',
  // 積極全体
  'totalPositive',
  // 差分
  'totalDiff',
  // ユーザー内消極
  'currentUserNegative',
  // ユーザー内積極
  'currentUserPositive',
  // 差分
  'currentUserDiff',
  // 消極認知未購入
  'recognizedNegative',
  // 積極認知未購入
  'recognizedPositive',
  // 差分
  'recognizedDiff',
  // 消極離反
  'estrangedNegative',
  // 積極離反
  'estrangedPositive',
  // 差分
  'estrangedDiff',
  // 消極一般
  'generalNegative',
  // 積極一般
  'generalPositive',
  // 差分
  'generalDiff',
  // 消極ロイヤル
  'loyalNegative',
  // 積極ロイヤル
  'loyalPositive',
  // 差分
  'loyalDiff',
  // 積極認知未購入と積極一般の差分
  'recognizedPositiveGeneralPositiveDiff',
  // 消極認知未購入と積極一般の差分
  'recognizedNegativeGeneralPositiveDiff',
  // 積極離反と積極一般の差分
  'estrangedPositiveGeneralPositiveDiff',
  // 消極離反と積極一般の差分
  'estrangedNegativeGeneralPositiveDiff',
  // 積極一般と積極ロイヤルの差分
  'generalPositiveLoyalPositiveDiff',
  // 消極一般と積極ロイヤルの差分
  'generalNegativeLoyalPositiveDiff',
  // 消極認知未購入と積極ロイヤルの差分
  'recognizedNegativeLoyalPositiveDiff',
  // 消極離反と積極ロイヤルの差分
  'estrangedNegativeLoyalPositiveDiff',
] as const
export type NineSegmentIndicator = (typeof nineSegmentIndicators)[number]

export const nineSegmentIndicatorLabelI18nKeyMap: Record<
  NineSegmentIndicator,
  keyof TranslationSchema
> = {
  totalNegative: 'passive_overall_1',
  totalPositive: 'active_overall_1',
  totalDiff: 'difference',
  currentUserNegative: 'userpassive',
  currentUserPositive: 'useractive',
  currentUserDiff: 'difference_1',
  recognizedNegative: 'passiveaware_noncustomers',
  recognizedPositive: 'activeaware_noncustomers',
  recognizedDiff: 'difference_2',
  estrangedNegative: 'passivedefected_customers',
  estrangedPositive: 'activedefected_customers',
  estrangedDiff: 'difference_3',
  generalNegative: 'passiveregular_customers',
  generalPositive: 'activeregular_customers',
  generalDiff: 'difference_4',
  loyalNegative: 'passiveloyal_customers',
  loyalPositive: 'activeloyal_customers',
  loyalDiff: 'difference_5',
  recognizedPositiveGeneralPositiveDiff: 'difference_6',
  recognizedNegativeGeneralPositiveDiff: 'difference_7',
  estrangedPositiveGeneralPositiveDiff: 'difference_8',
  estrangedNegativeGeneralPositiveDiff: 'difference_9',
  generalPositiveLoyalPositiveDiff: 'difference_10',
  generalNegativeLoyalPositiveDiff: 'difference_11',
  recognizedNegativeLoyalPositiveDiff: 'difference_12',
  estrangedNegativeLoyalPositiveDiff: 'difference_13',
}

export const nineSegmentIndicatorHelpMap: Record<
  NineSegmentIndicator,
  string | undefined
> = {
  totalNegative: '8+6+4+2',
  totalPositive: '7+5+3+1',
  totalDiff: undefined,
  currentUserNegative: '4+2',
  currentUserPositive: '3+1',
  currentUserDiff: undefined,
  recognizedNegative: '8',
  recognizedPositive: '7',
  recognizedDiff: undefined,
  estrangedNegative: '6',
  estrangedPositive: '5',
  estrangedDiff: undefined,
  generalNegative: '4',
  generalPositive: '3',
  generalDiff: undefined,
  loyalNegative: '2',
  loyalPositive: '1',
  loyalDiff: undefined,
  recognizedPositiveGeneralPositiveDiff: undefined,
  recognizedNegativeGeneralPositiveDiff: undefined,
  estrangedPositiveGeneralPositiveDiff: undefined,
  estrangedNegativeGeneralPositiveDiff: undefined,
  generalPositiveLoyalPositiveDiff: undefined,
  generalNegativeLoyalPositiveDiff: undefined,
  recognizedNegativeLoyalPositiveDiff: undefined,
  estrangedNegativeLoyalPositiveDiff: undefined,
}

export type NineSegmentColumnKey =
  | NineSegment
  | NineSegmentIndicator
  | 'answerNumber'
  | 'answerText'
  | 'currentUserPad'
  | 'estrangedNegativeGeneralPositivePad'
  | 'estrangedNegativeLoyalPositivePad'
  | 'estrangedPad'
  | 'estrangedPositiveGeneralPositivePad'
  | 'generalNegativeLoyalPositivePad'
  | 'generalPad'
  | 'generalPositiveLoyalPositivePad'
  | 'loyalPad'
  | 'questionText'
  | 'recognizedNegativeGeneralPositivePad'
  | 'recognizedNegativeLoyalPositivePad'
  | 'recognizedPad'
  | 'recognizedPositiveGeneralPositivePad'
  | 'total'
  | 'totalPad'

export const nineSegmentSortColumnKeys = [
  'total',
  ...nineSegments,
  'totalNegative',
  'totalPositive',
  'totalDiff',
  'currentUserNegative',
  'currentUserPositive',
  'currentUserDiff',
  'recognizedDiff',
  'estrangedDiff',
  'generalDiff',
  'loyalDiff',
  'recognizedPositiveGeneralPositiveDiff',
  'recognizedNegativeGeneralPositiveDiff',
  'estrangedPositiveGeneralPositiveDiff',
  'estrangedNegativeGeneralPositiveDiff',
  'generalPositiveLoyalPositiveDiff',
  'generalNegativeLoyalPositiveDiff',
  'recognizedNegativeLoyalPositiveDiff',
  'estrangedNegativeLoyalPositiveDiff',
] as const satisfies readonly NineSegmentColumnKey[]
export type NineSegmentSortColumnKey =
  (typeof nineSegmentSortColumnKeys)[number]

export const nineSegmentDisplayColumnKeys = [
  'total',
  ...nineSegments,
  'totalDiff',
  'currentUserDiff',
  'recognizedDiff',
  'estrangedDiff',
  'generalDiff',
  'loyalDiff',
  'recognizedPositiveGeneralPositiveDiff',
  'recognizedNegativeGeneralPositiveDiff',
  'estrangedPositiveGeneralPositiveDiff',
  'estrangedNegativeGeneralPositiveDiff',
  'generalPositiveLoyalPositiveDiff',
  'generalNegativeLoyalPositiveDiff',
  'recognizedNegativeLoyalPositiveDiff',
  'estrangedNegativeLoyalPositiveDiff',
] as const satisfies readonly NineSegmentColumnKey[]
export type NineSegmentDisplayColumnKey =
  (typeof nineSegmentDisplayColumnKeys)[number]

export const calcNineSegmentIndicatorCounts = (
  nineSegmentCounts: NineSegmentCounts,
): Record<NineSegmentIndicator, number> => {
  const totalNegative = sumNineNumbers(nineSegmentCounts, 8, 6, 4, 2)
  const totalPositive = sumNineNumbers(nineSegmentCounts, 7, 5, 3, 1)
  const currentUserNegative = sumNineNumbers(nineSegmentCounts, 4, 2)
  const currentUserPositive = sumNineNumbers(nineSegmentCounts, 3, 1)

  return {
    totalNegative,
    totalPositive,
    totalDiff: totalPositive - totalNegative,
    currentUserNegative,
    currentUserPositive,
    currentUserDiff: currentUserPositive - currentUserNegative,
    recognizedNegative: nineSegmentCounts.nineSegment8,
    recognizedPositive: nineSegmentCounts.nineSegment7,
    recognizedDiff: diffNineNumbers(nineSegmentCounts, 7, 8),
    estrangedNegative: nineSegmentCounts.nineSegment6,
    estrangedPositive: nineSegmentCounts.nineSegment5,
    estrangedDiff: diffNineNumbers(nineSegmentCounts, 5, 6),
    generalNegative: nineSegmentCounts.nineSegment4,
    generalPositive: nineSegmentCounts.nineSegment3,
    generalDiff: diffNineNumbers(nineSegmentCounts, 3, 4),
    loyalNegative: nineSegmentCounts.nineSegment2,
    loyalPositive: nineSegmentCounts.nineSegment1,
    loyalDiff: diffNineNumbers(nineSegmentCounts, 1, 2),
    recognizedPositiveGeneralPositiveDiff: diffNineNumbers(
      nineSegmentCounts,
      3,
      7,
    ),
    recognizedNegativeGeneralPositiveDiff: diffNineNumbers(
      nineSegmentCounts,
      3,
      8,
    ),
    estrangedPositiveGeneralPositiveDiff: diffNineNumbers(
      nineSegmentCounts,
      3,
      5,
    ),
    estrangedNegativeGeneralPositiveDiff: diffNineNumbers(
      nineSegmentCounts,
      3,
      6,
    ),
    generalPositiveLoyalPositiveDiff: diffNineNumbers(nineSegmentCounts, 1, 3),
    generalNegativeLoyalPositiveDiff: diffNineNumbers(nineSegmentCounts, 1, 4),
    recognizedNegativeLoyalPositiveDiff: diffNineNumbers(
      nineSegmentCounts,
      1,
      8,
    ),
    estrangedNegativeLoyalPositiveDiff: diffNineNumbers(
      nineSegmentCounts,
      1,
      6,
    ),
  }
}

export type NineSegmentsData = {
  counts: Record<NineSegment | NineSegmentIndicator | 'total', number>
  ratios: Record<NineSegment | NineSegmentIndicator | 'total', number>
}
